/**
 * Environment Configuration
 *
 * Centralized configuration for environment variables and API endpoints.
 * Uses Vite's import.meta.env for environment variable access.
 */

export interface EnvironmentConfig {
  apiBaseUrl: string;
  environment: 'development' | 'staging' | 'production';
  enableMockData: boolean;
  apiTimeout: number;
  pollingInterval: number;
}

/**
 * Get environment type from NODE_ENV or MODE
 */
function getEnvironment(): 'development' | 'staging' | 'production' {
  const mode = import.meta.env.MODE;
  const env = import.meta.env.VITE_APP_ENV;

  if (env === 'production' || mode === 'production') {
    return 'production';
  }
  if (env === 'staging' || mode === 'staging') {
    return 'staging';
  }
  return 'development';
}

/**
 * Environment configuration
 */
export const environment: EnvironmentConfig = {
  // API Base URL - defaults to local development server
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL || 'http://localhost:4010',

  // Current environment
  environment: getEnvironment(),

  // Enable mock data (useful for development without backend)
  enableMockData: import.meta.env.VITE_ENABLE_MOCK_DATA === 'true' || false,

  // API request timeout (milliseconds)
  apiTimeout: parseInt(import.meta.env.VITE_API_TIMEOUT || '30000', 10),

  // Polling interval for real-time updates (milliseconds)
  pollingInterval: parseInt(import.meta.env.VITE_POLLING_INTERVAL || '30000', 10),
};

/**
 * API Endpoints Configuration
 */
export const apiEndpoints = {
  // Health & Status
  health: '/health',
  fleetStatus: '/api/fleet/status',

  // Repository Management
  repositories: '/api/repos',
  repository: (fullName: string) => `/api/repos/${encodeURIComponent(fullName)}`,
  registerRepository: '/api/repos/register',
  syncRepository: (fullName: string) => `/api/repos/sync/${encodeURIComponent(fullName)}`,
  updateRepositoryTags: (fullName: string) => `/api/repos/${encodeURIComponent(fullName)}/tags`,

  // Feature Packs
  features: '/api/features',
  feature: (name: string, version?: string) =>
    version ? `/api/features/${encodeURIComponent(name)}/${version}` : `/api/features/${encodeURIComponent(name)}`,

  // Deployments
  deployments: '/api/deployments',
  deployment: (deploymentId: string) => `/api/deployments/${deploymentId}`,
  deployFeature: '/api/deploy',
  cancelDeployment: (deploymentId: string) => `/api/deployments/${deploymentId}/cancel`,
  rollbackDeployment: (deploymentId: string) => `/api/deployments/${deploymentId}/rollback`,

  // Tickets
  tickets: '/api/tickets',
  ticket: (ticketId: string) => `/api/tickets/${ticketId}`,
  createTickets: '/api/tickets/create',

  // Workflows
  workflows: '/api/workflows',
  workflow: (workflowId: string) => `/api/workflows/${workflowId}`,
  cancelWorkflow: (workflowId: string) => `/api/workflows/${workflowId}`,
  retryWorkflow: (workflowId: string) => `/api/workflows/${workflowId}/retry`,
};

/**
 * Feature flags
 */
export const features = {
  enableWorkflows: import.meta.env.VITE_FEATURE_WORKFLOWS !== 'false',
  enableAIAssist: import.meta.env.VITE_FEATURE_AI_ASSIST === 'true',
  enableRealTimeUpdates: import.meta.env.VITE_FEATURE_REALTIME === 'true',
};

/**
 * Validation helpers
 */
export const isProduction = () => environment.environment === 'production';
export const isDevelopment = () => environment.environment === 'development';
export const isStaging = () => environment.environment === 'staging';

/**
 * Log configuration in development
 */
if (isDevelopment()) {
  console.log('Environment Configuration:', {
    apiBaseUrl: environment.apiBaseUrl,
    environment: environment.environment,
    enableMockData: environment.enableMockData,
    features,
  });
}

export default environment;
